import api from './api';

// ─── Visitor ID ──────────────────────────────────────────────────────────────
// A stable anonymous ID stored in localStorage so we can track unique visitors
// across sessions without any personal data.
const VISITOR_KEY = 'analytics_visitor_id';

function getVisitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = 'v_' + Math.random().toString(36).slice(2, 11) + '_' + Date.now().toString(36);
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return 'v_unknown';
  }
}

// ─── Page load start time ─────────────────────────────────────────────────────
// Captured as early as possible so we can compute the "time to first page load".
const PAGE_LOAD_START = window.performance?.timing?.navigationStart || Date.now();

// ─── Session start ────────────────────────────────────────────────────────────
// Track whether this is the first page load in this session.
const SESSION_KEY = 'analytics_session_start';
let isFirstLoad = false;
try {
  if (!sessionStorage.getItem(SESSION_KEY)) {
    sessionStorage.setItem(SESSION_KEY, Date.now().toString());
    isFirstLoad = true;
  }
} catch { /* ignore */ }

// ─── Core helpers ─────────────────────────────────────────────────────────────

/** Fire-and-forget POST — never throws so it never breaks the app */
async function send(payload) {
  try {
    await api.post('/analytics/event', payload);
  } catch {
    // silently ignore — analytics must never break the app
  }
}

function buildBase(extra = {}) {
  return {
    visitorId: getVisitorId(),
    page: window.location.pathname,
    referrer: document.referrer || null,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    ...extra,
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Call this whenever the route changes (or on first mount).
 * @param {string} path  – e.g. "/dashboard"
 * @param {object|null} user – current auth user (may be null for anon views)
 */
export function trackPageView(path, user = null) {
  send(buildBase({
    event: 'page_view',
    page: path,
    userId: user?.id || null,
    userEmail: user?.email || null,
  }));
}

/**
 * Call once after the app first becomes interactive.
 * @param {number} loadTimeMs – milliseconds from navigation start to interactive
 */
export function trackLoadTime(loadTimeMs) {
  if (!isFirstLoad) return; // only track once per session
  send(buildBase({
    event: 'initial_load',
    loadTimeMs,
  }));
}

/**
 * Call after a successful login / Google OAuth.
 * @param {object} user – the authenticated user object
 */
export function trackLogin(user) {
  send(buildBase({
    event: 'login',
    userId: user?.id || null,
    userEmail: user?.email || null,
    method: user?.googleId ? 'google' : 'email',
  }));
}

// Export load start so App.jsx can compute elapsed time
export { PAGE_LOAD_START };
